import React, { useMemo, useRef } from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import dayjs from "dayjs";
import { PrimaryButton } from "../buttons/PrimaryButton";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { Tooltip } from "@material-tailwind/react";
import InformationIcon from "../../assets/icons/info/information.png";
import { getStoreColor } from "../../constants/plasticColors";

interface IPlasticActivationDataPoint {
  period: string;
  date: string;
  store_name?: string;
  plastic_activation?: number;
  plastic_activation_percentage?: number;
  week_start?: string;
  week_end?: string;
  month_start?: string;
  year?: number;
  quarter?: string;
}

interface PlasticActivationProps {
  data: IPlasticActivationDataPoint[] | null;
  isLoading: boolean;
  period: "weekly" | "monthly" | "quarterly" | "yearly";
  from?: string | null;
  to?: string | null;
}

export const PlasticActivation = React.memo(
  ({ data, isLoading, period, from, to }: PlasticActivationProps) => {
    const snapshotRef = useRef(null);
    const [, takeScreenshot] = useScreenshot();

    const downloadScreenshot = () => {
      takeScreenshot(snapshotRef.current).then((image: any) => {
        const link = document.createElement("a");
        link.href = image;
        link.download = "plastic_activation.png";
        link.click();
      });
    };

    // Transform data into chart format
    const { categories, series, seriesNames } = useMemo(() => {
      if (!data || data.length === 0) {
        return { categories: [], series: [], seriesNames: [] };
      }

      // Group data by location
      const groupedData: Record<string, Record<string, number>> = {};
      const allDates = new Set<string>();

      data.forEach((point) => {
        const key = point.store_name;
        if (!key) return;

        let dateKey = "";
        if (period === "weekly" && point.week_start) {
          dateKey = dayjs(point.week_start).format("YYYY-MM-DD");
        } else if (period === "monthly") {
          dateKey = dayjs(point.date).format("YYYY-MM");
        } else if (period === "quarterly") {
          dateKey = `${point.year} ${point.quarter}`;
        } else if (period === "yearly") {
          dateKey = String(point.year);
        }

        allDates.add(dateKey);
        if (!groupedData[key]) {
          groupedData[key] = {};
        }
        groupedData[key][dateKey] =
          point.plastic_activation_percentage ?? point.plastic_activation ?? 0;
      });

      // Sort dates
      const sortedDates = Array.from(allDates).sort((a, b) => {
        if (period === "weekly") {
          return dayjs(a).valueOf() - dayjs(b).valueOf();
        } else if (period === "monthly") {
          return dayjs(a).valueOf() - dayjs(b).valueOf();
        } else if (period === "quarterly") {
          return a.localeCompare(b);
        } else {
          return a.localeCompare(b);
        }
      });

      // Format categories for display
      const categories = sortedDates.map((date) => {
        if (period === "weekly") {
          return dayjs(date).format("MMM DD");
        } else if (period === "monthly") {
          return dayjs(date).format("MMM");
        } else if (period === "quarterly") {
          return date;
        } else {
          return date;
        }
      });

      // Create series for each location
      const seriesNames = Object.keys(groupedData).sort();
      const series = seriesNames.map((name) => ({
        name,
        data: sortedDates.map((date) => groupedData[name][date] || 0),
      }));

      return { categories, series, seriesNames };
    }, [data, period]);

    // Map colors based on series names
    const colors = useMemo(
      () => seriesNames.map((name) => getStoreColor(name)),
      [seriesNames]
    );

    const options: ApexOptions = {
      chart: {
        type: "line",
        height: 350,
        zoom: { enabled: false },
        toolbar: { show: false },
      },
      stroke: {
        curve: "smooth",
        width: 3,
      },
      dataLabels: { enabled: false },
      grid: {
        strokeDashArray: 5,
        padding: {
          right: 30,
          left: 10,
        },
      },
      xaxis: {
        title: {
          text: "Month",
        },
        categories,
        labels: {
          rotate: -45,
          trim: false,
          style: {
            cssClass: "apexcharts-xaxis-label",
          },
        },
      },
      yaxis: {
        title: {
          text: "Plastic Droppers over Total Droppers at that location (%)",
        },
        labels: {
          formatter: (value: number) => {
            return (
              value.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }) + "%"
            );
          },
        },
      },
      colors: colors.length > 0 ? colors : ["#808080"],
      legend: {
        position: "bottom",
        horizontalAlign: "center",
        markers: { size: 2 },
      },
      markers: {
        size: 4,
        hover: {
          size: 6,
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: function ({ series, dataPointIndex, w }: any) {
          const seriesData = series
            .map((seriesValues: any, index: number) => ({
              name: w.globals.seriesNames[index],
              value: seriesValues[dataPointIndex],
              color: w.config.colors[index],
            }))
            .filter(
              (item: any) => item.value !== undefined && item.value !== null
            )
            .sort((a: any, b: any) => (b.value || 0) - (a.value || 0));

          let html =
            '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">' +
            (categories?.[dataPointIndex] ?? "") +
            "</div>";

          seriesData.forEach((item: any) => {
            const value = item.value ?? 0;
            html += `
            <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
              <span class="apexcharts-tooltip-marker" style="background-color: ${item.color};"></span>
              <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
                <div class="apexcharts-tooltip-y-group">
                  <span class="apexcharts-tooltip-text-y-label">${item.name}: </span>
                  <span class="apexcharts-tooltip-text-y-value">${value?.toLocaleString(
                    undefined,
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }
                  )}%</span>
                </div>
              </div>
            </div>`;
          });

          return html;
        },
      },
    };

    // Format date range for display
    const dateRangeText = useMemo(() => {
      if (!from || !to) return null;
      const fromDate = dayjs(from);
      const toDate = dayjs(to);

      if (period === "monthly") {
        return `${fromDate.format("MMM YYYY")} - ${toDate.format("MMM YYYY")}`;
      } else if (period === "weekly") {
        return `${fromDate.format("MMM DD, YYYY")} - ${toDate.format("MMM DD, YYYY")}`;
      } else if (period === "quarterly") {
        return `${fromDate.format("YYYY")} Q${Math.ceil((fromDate.month() + 1) / 3)}`;
      } else if (period === "yearly") {
        return `${fromDate.format("YYYY")} - ${toDate.format("YYYY")}`;
      }
      return null;
    }, [from, to, period]);

    if (isLoading) {
      return (
        <div className="min-h-[400px] flex items-center justify-center">
          <div className="text-gray-500">Loading...</div>
        </div>
      );
    }

    if (!data || data.length === 0) {
      return (
        <div className="min-h-[400px] flex items-center justify-center">
          <div className="text-gray-500">No data available</div>
        </div>
      );
    }

    return (
      <div ref={snapshotRef} className="w-full">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">
                Non Cumulative Plastic Conversion Per Location
              </h2>
              <Tooltip
                content="Non-cumulative: Shows plastic conversion per location for each selected time period"
                placeholder={undefined}
              >
                <img
                  src={InformationIcon}
                  alt="info"
                  className="inline-block w-4 h-4 cursor-pointer"
                />
              </Tooltip>
            </div>
            {dateRangeText && (
              <p className="text-sm text-gray-500 mt-1">{dateRangeText}</p>
            )}
          </div>
          <PrimaryButton
            size="sm"
            onClick={downloadScreenshot}
            label="Download image"
          />
        </div>
        <div>
          <Chart options={options} series={series} type="line" height={350} />
        </div>
      </div>
    );
  }
);

PlasticActivation.displayName = "PlasticActivation";
